import { useRollbar } from "@rollbar/react";
import classNames from "classnames";
import React from "react";
import { useEffect, useRef } from "react";

import { HelpLink } from "@components/user/editor/partials/editorExamDetail/helpLink/HelpLink";

import { Icon, Msg, Select, Spinner } from "@shared/components";
import { useLocalStorage } from "@shared/hooks";
import { useWebcam } from "@shared/hooks/useWebcam";
import { LocalStorageConstants } from "@shared/services/enums";

import { getDeviceLabelById } from "../../../../editor/partials/editorMain/partial/webcamEventHandler/WebcamEventUtils";

interface WebcamSetupPanelProps {
  onWebcamConnect: () => void;
  onWebcamDisconnect: () => void;
}

export const WebcamSetupPanel = ({
  onWebcamConnect,
  onWebcamDisconnect,
}: WebcamSetupPanelProps) => {
  const rollbar = useRollbar();

  const onConnectRef = useRef(onWebcamConnect);
  const onDisconnectRef = useRef(onWebcamDisconnect);

  useEffect(() => {
    onConnectRef.current = onWebcamConnect;
    onDisconnectRef.current = onWebcamDisconnect;
  }, [onWebcamConnect, onWebcamDisconnect]);

  const [localStorageWebcamId, setLocalStorageWebcamId] = useLocalStorage(
    LocalStorageConstants.Webcam,
    "",
  );
  const [_localStorageWebcamLabel, setLocalStorageWebcamLabel] =
    useLocalStorage(LocalStorageConstants.WebcamLabel, "");

  const {
    isCapturing,
    selectedVideoTrack,
    videoTracks,
    isLoading,
    setSelectedVideoTrack,
    videoRef,
  } = useWebcam({
    defaultWebcamId: localStorageWebcamId,
    onConnect: onConnectRef.current,
    onDisconnect: onDisconnectRef.current,
  });

  useEffect(() => {
    if (selectedVideoTrack) {
      // This will give us the device id, which is consistent across page reloads, but not the label.
      const deviceId = (selectedVideoTrack as MediaStreamTrack)?.getSettings()
        .deviceId;

      if (deviceId) {
        setLocalStorageWebcamId(deviceId);
        getDeviceLabelById(deviceId).then((response) => {
          if (response && response.label) {
            setLocalStorageWebcamLabel(response.label);
          } else {
            rollbar.error(response?.error || "Unable to get webcam label");
          }
        });
      }
    }
  }, [selectedVideoTrack]);

  const rootStyle = classNames("code-webcam-setup-panel");

  return (
    <div className={rootStyle}>
      <div className="code-webcam-setup-panel__webcam">
        <video
          ref={videoRef}
          muted
          className={classNames(
            "code-webcam-setup-panel__webcam__video-dimensions",
            {
              "is-not-capturing": !isCapturing,
            },
          )}
        />
        {isCapturing && (
          <Select
            selectClassName="code-webcam-setup-panel__webcam__select"
            value={selectedVideoTrack?.id}
            options={videoTracks?.map((videoTrack) => ({
              value: videoTrack.id,
              label: videoTrack.label,
            }))}
            onChange={(event: React.FormEvent<HTMLSelectElement>) => {
              const { value } = event.target as HTMLSelectElement;

              const deviceId = videoTracks
                ?.find((track) => track.id === value)
                ?.getSettings().deviceId;

              setSelectedVideoTrack(
                videoTracks?.find((track) => track.id === value) ?? null,
              );

              if (deviceId) {
                setLocalStorageWebcamId(deviceId);
              }
              setLocalStorageWebcamLabel(
                videoTracks?.find((track) => track.id === value)?.label ?? "",
              );
            }}
          />
        )}

        {!isCapturing && (
          <>
            <div className="code-webcam-setup-panel__webcam__video-dimensions no-camera">
              {isLoading && <Spinner size="small" color="gray" />}
              {!isLoading && <Icon type="exclamation-triangle" />}
              <Msg
                className="text-sm"
                id="start-challenge.webcam.pleaseTurnOnCamera"
              />
            </div>
            <HelpLink />
          </>
        )}
      </div>
      <div className="code-webcam-setup-panel__warning">
        <ul>
          <li>
            <span>
              1. <Msg id="start-challenge.webcam.warning1" />
            </span>
          </li>
          <li>
            <span>
              2. <Msg id="start-challenge.webcam.warning2" />
            </span>
          </li>
        </ul>
      </div>
    </div>
  );
};
